import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  ClipboardList,
  FilePlus2,
  FileUp,
  FileX2,
  Hourglass,
  Link2,
  Loader2,
  ServerCrash,
  XCircle,
  type LucideIcon,
} from 'lucide-react'
import type { Alert } from '@/types/alerts'
import { useAlerts } from './useAlerts'

type AlertIcon = { Icon: LucideIcon; label: string; className: string }

const NEUTRAL = 'text-muted'
const DANGER = 'text-red-600 dark:text-red-400'
const OK = 'text-green-600 dark:text-green-400'
const WARN = 'text-amber-600 dark:text-amber-400'

const ICON_BY_TYPE: Record<string, AlertIcon> = {
  fluxo_parado: { Icon: AlertTriangle, label: 'Fluxo parado', className: WARN },
  process_task_assigned: {
    Icon: ClipboardList,
    label: 'Tarefa atribuída',
    className: NEUTRAL,
  },
  solicitacao_awaiting_approval: {
    Icon: Hourglass,
    label: 'Aguardando aprovação',
    className: WARN,
  },
  solicitacao_approved: {
    Icon: CheckCircle2,
    label: 'Solicitação aprovada',
    className: OK,
  },
  solicitacao_rejected: {
    Icon: XCircle,
    label: 'Solicitação rejeitada',
    className: DANGER,
  },
  criacao_api_falhou: {
    Icon: ServerCrash,
    label: 'Falha na criação por API',
    className: DANGER,
  },
}

/** Só avisos de criação têm `status`: o ícone acompanha o estado (o mesmo aviso muda no lugar). */
const ICON_BY_CREATION_STATUS: Record<string, AlertIcon> = {
  em_criacao: {
    Icon: Loader2,
    label: 'Em criação',
    className: `${NEUTRAL} animate-spin`,
  },
  criado: { Icon: FilePlus2, label: 'Processo criado', className: OK },
  ja_existia: { Icon: Link2, label: 'Processo já existia', className: NEUTRAL },
  atualizado: { Icon: FileUp, label: 'Processo atualizado', className: OK },
  falhou: { Icon: FileX2, label: 'Falha na criação', className: DANGER },
}

const FALLBACK: AlertIcon = {
  Icon: Bell,
  label: 'Notificação',
  className: NEUTRAL,
}

const DIA_HORA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

/** `08/10 14:32` — o Intl do pt-BR põe vírgula entre data e hora. */
function quando(iso: string): string {
  return DIA_HORA.format(new Date(iso)).replace(',', '')
}

function iconFor(a: Alert): AlertIcon {
  if (a.status) return ICON_BY_CREATION_STATUS[a.status] ?? FALLBACK
  return ICON_BY_TYPE[a.type] ?? FALLBACK
}

export const AlertBell: React.FC = () => {
  const navigate = useNavigate()
  const { items, unreadCount, markRead, markAllRead } = useAlerts()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const badgeCount = Math.max(0, unreadCount)

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        title="Notificações"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2"
      >
        <Bell className="h-5 w-5 text-body/80" />
        {badgeCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[11px] text-white">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-0.5 w-80 rounded-[5px] border border-border-subtle bg-surface shadow-xl shadow-black/10 dark:shadow-black/60">
          <div className="flex items-center justify-between border-b border-border-faint p-3">
            <h3 className="text-sm font-semibold text-body/80">Notificações</h3>
            {badgeCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead()}
                className="text-xs text-[#2563eb] hover:underline"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="p-4 text-sm text-muted">Sem notificações.</p>
            ) : (
              items.map((a) => {
                const { Icon, label, className } = iconFor(a)
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      // Em criação não se marca como lido: some quando o processo nascer.
                      if (a.status !== 'em_criacao') markRead(a.id)
                      // Só fecha quando sai da página; sem link a lista fica aberta.
                      if (a.link) {
                        setOpen(false)
                        navigate(a.link)
                      }
                    }}
                    className="flex w-full gap-2.5 border-b border-border-faint p-3 text-left hover:bg-surface-muted"
                  >
                    <Icon
                      role="img"
                      aria-label={label}
                      className={`mt-0.5 h-4 w-4 flex-shrink-0 ${className}`}
                    />
                    <div className="min-w-0">
                      <p
                        className={`text-sm font-medium ${
                          a.status === 'falhou'
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-body/80'
                        }`}
                      >
                        {a.title}
                      </p>
                      {/* whitespace-pre-line: o corpo vem com uma informação por
                      linha (nome da solicitação, quem abriu e quando, motivo).
                      Sem isso as quebras colapsam num parágrafo único. */}
                      {a.body && (
                        <p className="mt-0.5 whitespace-pre-line text-xs text-muted">
                          {a.body}
                        </p>
                      )}
                      {/* updated_at: quando o aviso chegou no estado atual (em
                      criação → criado volta a chegar). Empresa só vem para
                      quem enxerga mais de uma (ex.: desenvolvedor). */}
                      <p className="mt-1 text-[11px] text-muted">
                        {a.company_name && (
                          <>
                            <span className="font-medium">{a.company_name}</span>
                            {' · '}
                          </>
                        )}
                        <time
                          dateTime={a.updated_at}
                          title={new Date(a.updated_at).toLocaleString('pt-BR')}
                        >
                          {quando(a.updated_at)}
                        </time>
                      </p>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
